import React, { useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { detectPlaceholders, getAtPath } from "../../lib/placeholders";

// ── Shared helpers ─────────────────────────────────────────────
const PLACEHOLDER_RE = /\[[^\]]+\]/;
const hasPlaceholder = v => typeof v === "string" && PLACEHOLDER_RE.test(v);
const fieldCls = v =>
  `w-full border px-2 py-1.5 text-xs focus:outline-none focus:ring-1 resize-none ${
    hasPlaceholder(v)
      ? "border-[#F59E0B] bg-[#FFFBEB] focus:ring-[#F59E0B]"
      : "border-border focus:ring-[#0025D8]"
  }`;

function Label({ children, extra }) {
  return (
    <div className="font-mono text-[9px] text-muted-foreground uppercase tracking-wider mb-0.5 flex items-center justify-between">
      <span>{children}</span>
      {extra}
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 pb-1 border-b border-border">
      {title}
    </div>
  );
}

// ── Placeholder row ────────────────────────────────────────────
function PlaceholderRow({ item, onFill }) {
  const [value, setValue] = useState("");

  const getHint = ph => {
    const inner = ph.slice(1, -1).toLowerCase();
    if (inner.includes("%") || inner === "x%") return "e.g. 40%";
    if (inner === "n" || inner.includes("number") || inner.includes("count")) return "e.g. 5";
    if (inner.includes("year") || inner.includes("date")) return "e.g. 2023";
    if (inner.includes("metric") || inner.includes("value")) return "e.g. 2×";
    return `Fill in ${ph}`;
  };

  const getFieldLabel = path => {
    if (path === "summary") return "Summary";
    if (path.startsWith("experience") && path.includes("bullets")) return "Experience bullet";
    if (path.startsWith("experience")) return "Experience";
    if (path.startsWith("education")) return "Education";
    if (path.startsWith("skills")) return "Skills";
    if (path.startsWith("certifications")) return "Certification";
    if (path.startsWith("contact")) return "Contact";
    return "Resume field";
  };

  const renderContext = (text, ph) => {
    const idx = text.indexOf(ph);
    if (idx === -1) return text.slice(0, 70);
    const start = Math.max(0, idx - 22);
    const end = Math.min(text.length, idx + ph.length + 22);
    return (
      <>
        {start > 0 && "…"}
        {text.slice(start, idx)}
        <span className="font-mono bg-amber-200 text-amber-800 px-0.5 rounded-sm">{ph}</span>
        {text.slice(idx + ph.length, end)}
        {end < text.length && "…"}
      </>
    );
  };

  const commit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onFill(item.path, item.text.replace(item.placeholder, trimmed));
  };

  return (
    <div className="border border-[#F59E0B]/50 bg-[#FFFBEB] p-3 mb-2 last:mb-0">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="font-mono text-[9px] bg-amber-100 text-amber-700 border border-amber-300 px-1.5 py-0.5 rounded-sm">
          {item.placeholder}
        </span>
        <span className="font-mono text-[9px] text-muted-foreground uppercase tracking-wider">
          {getFieldLabel(item.path)}
        </span>
      </div>
      <p className="text-[11px] text-muted-foreground italic mb-2 leading-relaxed">
        {renderContext(item.text, item.placeholder)}
      </p>
      <div className="flex gap-1.5">
        <input
          className="flex-1 border border-[#F59E0B] bg-white px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#F59E0B] font-mono"
          value={value}
          onChange={e => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
          placeholder={getHint(item.placeholder)}
        />
        {value.trim() && (
          <button
            onMouseDown={e => { e.preventDefault(); commit(); }}
            className="px-2 py-1 bg-[#F59E0B] text-white text-[10px] font-mono font-bold hover:bg-amber-500 flex-shrink-0"
          >
            ✓
          </button>
        )}
      </div>
    </div>
  );
}

// ── Experience block ───────────────────────────────────────────
function ExpBlock({ exp, index, onFieldChange }) {
  const [open, setOpen] = useState(index === 0);
  const title = exp.title || exp.company || `Role ${index + 1}`;

  const addBullet = () => {
    const next = [...(exp.bullets || []), ""];
    onFieldChange(`experience.${index}.bullets`, next);
  };

  const removeBullet = j => {
    const next = [...(exp.bullets || [])];
    next.splice(j, 1);
    onFieldChange(`experience.${index}.bullets`, next);
  };

  return (
    <div className="border border-border mb-2 last:mb-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-secondary/30"
      >
        <span className="text-xs font-semibold truncate">{title}</span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {(exp.bullets || []).some(hasPlaceholder) && (
            <span className="w-2 h-2 rounded-full bg-[#F59E0B]" title="Contains placeholders" />
          )}
          {open ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
        </div>
      </button>

      {open && (
        <div className="px-3 pb-3 border-t border-border pt-2 space-y-2">
          {[["title","Job Title"],["company","Company"],["location","Location"],["start_date","Start Date"],["end_date","End Date"]].map(([f, lbl]) => {
            const val = exp[f] || "";
            return (
              <div key={f}>
                <Label>{lbl}</Label>
                <input
                  className={fieldCls(val)}
                  value={val}
                  onChange={e => onFieldChange(`experience.${index}.${f}`, e.target.value)}
                />
              </div>
            );
          })}
          <div>
            <Label extra={
              <button type="button" onClick={addBullet} className="flex items-center gap-0.5 text-[#0025D8] hover:underline text-[9px]">
                <Plus className="w-2.5 h-2.5" /> Add
              </button>
            }>
              Bullets
            </Label>
            {(exp.bullets || []).map((b, j) => (
              <div key={j} className="flex gap-1 mb-1 items-start">
                <textarea
                  className={`${fieldCls(b)} flex-1 min-h-[36px] leading-tight`}
                  value={b}
                  onChange={e => onFieldChange(`experience.${index}.bullets.${j}`, e.target.value)}
                />
                <button type="button" onClick={() => removeBullet(j)} className="mt-1 text-muted-foreground hover:text-[#E11D48] flex-shrink-0">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────
export default function PlaceholderFillPanel({ data, onFieldChange }) {
  const items = detectPlaceholders(data);

  // Stable key per occurrence: count how many times we've seen this path+placeholder combo before index i
  const keyCounts = {};
  const itemsWithKeys = items.map(item => {
    const base = `${item.path}::${item.placeholder}`;
    const idx = keyCounts[base] || 0;
    keyCounts[base] = idx + 1;
    return { ...item, stableKey: `${base}::${idx}` };
  });

  return (
    <div>
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white border-b border-border px-4 py-2.5 flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Edit Resume</span>
        {items.length === 0 ? (
          <span className="font-mono text-[10px] text-[#10B981] flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Ready to download
          </span>
        ) : (
          <span className="font-mono text-[10px] text-amber-600 font-bold">{items.length} placeholder{items.length !== 1 ? "s" : ""} left</span>
        )}
      </div>

      <div className="p-4 space-y-5">

        {/* ── Placeholder section ──────────────────────────── */}
        {items.length > 0 ? (
          <div>
            <SectionHeader title="Fill in your values" />
            <p className="text-[11px] text-muted-foreground mb-3">
              The AI added placeholders where it couldn't determine exact metrics. Fill each one in — the preview updates live.
            </p>
            {itemsWithKeys.map(item => (
              <PlaceholderRow key={item.stableKey} item={item} onFill={onFieldChange} />
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2 bg-[#F0FDF4] border border-[#10B981]/30 px-3 py-2.5">
            <CheckCircle2 className="w-4 h-4 text-[#10B981] flex-shrink-0" />
            <p className="text-xs text-[#166534]">All placeholders filled — you can now download your resume.</p>
          </div>
        )}

        {/* ── Contact ──────────────────────────────────────── */}
        <div>
          <SectionHeader title="Contact" />
          <div className="space-y-1.5">
            {[
              ["contact.name", "Full Name"],
              ["contact.email", "Email"],
              ["contact.phone", "Phone"],
              ["contact.location", "Location"],
              ["contact.linkedin", "LinkedIn URL"],
              ["contact.website", "Website (optional)"],
            ].map(([path, lbl]) => {
              const val = getAtPath(data, path) || "";
              return (
                <div key={path}>
                  <Label>{lbl}</Label>
                  <input className={fieldCls(val)} value={val} onChange={e => onFieldChange(path, e.target.value)} />
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Summary ──────────────────────────────────────── */}
        <div>
          <SectionHeader title="Professional Summary" />
          <textarea
            className={`${fieldCls(data?.summary || "")} min-h-[70px]`}
            value={data?.summary || ""}
            onChange={e => onFieldChange("summary", e.target.value)}
          />
        </div>

        {/* ── Experience ───────────────────────────────────── */}
        {(data?.experience?.length > 0) && (
          <div>
            <SectionHeader title="Work Experience" />
            {data.experience.map((exp, i) => (
              <ExpBlock key={i} exp={exp} index={i} onFieldChange={onFieldChange} />
            ))}
          </div>
        )}

        {/* ── Education ────────────────────────────────────── */}
        {(data?.education?.length > 0) && (
          <div>
            <SectionHeader title="Education" />
            {data.education.map((edu, i) => (
              <div key={i} className="border border-border p-3 mb-2 last:mb-0 space-y-1.5">
                {[["degree","Degree"],["institution","Institution"],["graduation_date","Graduation Date"],["location","Location"]].map(([f, lbl]) => {
                  const val = edu[f] || "";
                  return (
                    <div key={f}>
                      <Label>{lbl}</Label>
                      <input className={fieldCls(val)} value={val} onChange={e => onFieldChange(`education.${i}.${f}`, e.target.value)} />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* ── Skills ───────────────────────────────────────── */}
        {(data?.skills?.length > 0) && (
          <div>
            <SectionHeader title="Skills" />
            {data.skills.map((sk, i) => (
              <div key={i} className="mb-2 last:mb-0 space-y-1">
                <Label>Category</Label>
                <input
                  className={fieldCls(sk.name || "")}
                  value={sk.name || ""}
                  onChange={e => onFieldChange(`skills.${i}.name`, e.target.value)}
                  placeholder="e.g. Programming Languages"
                />
                <Label>Skills (comma-separated)</Label>
                <input
                  className={`${fieldCls((sk.items || []).join(", "))} text-[11px]`}
                  value={(sk.items || []).join(", ")}
                  onChange={e => onFieldChange(`skills.${i}.items`, e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                  placeholder="React, TypeScript, Python…"
                />
              </div>
            ))}
          </div>
        )}

        {/* ── Certifications ───────────────────────────────── */}
        {(data?.certifications?.length > 0) && (
          <div>
            <SectionHeader title="Certifications" />
            {data.certifications.map((cert, i) => (
              <div key={i} className="grid grid-cols-3 gap-1 mb-2 last:mb-0">
                {[["name","Name"],["issuer","Issuer"],["date","Date"]].map(([f, lbl]) => {
                  const val = cert[f] || "";
                  return (
                    <div key={f}>
                      <Label>{lbl}</Label>
                      <input className={fieldCls(val)} value={val} onChange={e => onFieldChange(`certifications.${i}.${f}`, e.target.value)} />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
